/**
 * Uploading a document (F5b).
 *
 * The size is checked here as well as on the server, for a reason worth
 * stating: a 30 MB upload rejected by the server has already been sent over
 * the network, which on a site connection is a minute of waiting for a
 * refusal. Checking locally makes that instant. It is not a substitute for the
 * server's check — the server cannot trust a browser — it is a courtesy.
 *
 * The type is NOT checked here. `accept` filters the file dialog, and beyond
 * that only the server can tell whether a file's bytes match its declared
 * type, so guessing locally would produce a rejection that disagrees with the
 * real one.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AttachmentType, Confidentiality } from '@nexus/contracts';

import { useUploadAttachment } from '../../lib/attachments.queries';
import { formatFileSize } from '../../lib/format';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Field } from '../ui/Field';
import { Select } from '../ui/Select';

import {
  ACCEPTED_FILE_EXTENSIONS,
  ATTACHMENT_TYPES,
  CONFIDENTIALITY_LABELS,
} from './attachment-types';
import { LabelOnlyNotice } from './LabelOnlyNotice';

/** The server's cap (PRD §5.10), mirrored so the message can be specific. */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Exactly one of these — the attachment's owner. */
  startupId?: string;
  pilotId?: string;
}

export function UploadDialog({
  open,
  onOpenChange,
  startupId,
  pilotId,
}: UploadDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<AttachmentType>('PITCH_DECK');
  const [confidentiality, setConfidentiality] = useState<Confidentiality>('INTERNAL');
  const [error, setError] = useState<string | null>(null);
  /** Bumped to remount the file input — see `reset`. */
  const [inputGeneration, setInputGeneration] = useState(0);

  const upload = useUploadAttachment();

  /** Clears everything, so reopening the dialog does not resume a stale draft. */
  const reset = (): void => {
    setFile(null);
    setType('PITCH_DECK');
    setConfidentiality('INTERNAL');
    setError(null);

    /* A file input's value cannot be assigned from React, and reopening the
       dialog would otherwise still show the previous filename beside a picker
       whose state has been reset. Bumping a key REMOUNTS the input, which
       clears the browser's own selection — no ref, and no unreachable null
       guard around one. */
    setInputGeneration((generation) => generation + 1);
  };

  const close = (): void => {
    reset();
    onOpenChange(false);
  };

  const onSubmit = (): void => {
    if (file === null) {
      setError(t('attachment.upload.noFile'));
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      /* Names both figures. "Too large" leaves the user guessing how much to
         cut; "31 MB, over the 25 MB limit" tells them whether to compress or
         to split. */
      setError(
        t('attachment.upload.tooLarge', {
          size: formatFileSize(file.size),
          max: formatFileSize(MAX_UPLOAD_BYTES),
        }),
      );
      return;
    }

    setError(null);
    upload.mutate(
      {
        file,
        fields: {
          type,
          confidentiality,
          ...(startupId === undefined ? {} : { startupId }),
          ...(pilotId === undefined ? {} : { pilotId }),
        },
      },
      {
        onSuccess: close,
        /* The server's reasons are more specific than anything guessable
           here — a renamed file, a type outside the allowlist — so its status
           picks the message rather than a generic failure. */
        onError: (cause: unknown) => {
          const status =
            typeof cause === 'object' && cause !== null && 'status' in cause
              ? (cause as { status?: number }).status
              : undefined;

          setError(
            status === 415
              ? t('attachment.upload.typeNotAllowed')
              : status === 413
                ? t('attachment.upload.tooLarge', {
                    size: formatFileSize(file.size),
                    max: formatFileSize(MAX_UPLOAD_BYTES),
                  })
                : t('attachment.upload.failed'),
          );
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      /* Radix only reports a CLOSE here — it has no way to open itself, since
         `open` is controlled from above — so there is no `if (!next)` guard:
         one would be a branch no test could reach. */
      onOpenChange={() => {
        reset();
        onOpenChange(false);
      }}
      title={t('attachment.upload.title')}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={upload.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={upload.isPending}>
            {upload.isPending
              ? t('attachment.upload.submitting')
              : t('attachment.upload.submit')}
          </Button>
        </>
      }
    >
      <div className="gap-s3 flex flex-col">
        {/* Stated before the file is chosen, not after it is uploaded. PRD
            §5.10 requires the interface to say that confidentiality is a
            label — and the moment it matters is while someone is deciding
            whether to upload the document at all. */}
        <LabelOnlyNotice />

        <Field
          label={t('attachment.upload.file')}
          hint={t('attachment.upload.fileHint', {
            max: formatFileSize(MAX_UPLOAD_BYTES),
          })}
          error={error ?? undefined}
          required
        >
          {({ id, errorId, invalid, required, ...described }) => (
            <div className="gap-s2 flex flex-col items-start">
              <input
                key={inputGeneration}
                id={id}
                type="file"
                required={required}
                accept={ACCEPTED_FILE_EXTENSIONS}
                aria-invalid={invalid}
                aria-errormessage={invalid ? errorId : undefined}
                {...described}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setError(null);
                }}
                className="text-body file:mr-s2 file:font-display block w-full text-sm file:rounded-full file:border-0 file:bg-navy file:px-4 file:py-2 file:text-xs file:font-extrabold file:uppercase file:tracking-[0.1em] file:text-white"
              />
              {/* The size beside the name, so a file near the cap is visible
                  before the upload rather than after it fails. */}
              {file === null ? null : (
                <span className="text-body text-xs tabular-nums">
                  {formatFileSize(file.size)}
                </span>
              )}
            </div>
          )}
        </Field>

        <Field label={t('attachment.upload.type')} required>
          {({ id }) => (
            <Select
              id={id}
              value={type}
              onValueChange={(value) => {
                setType(value as AttachmentType);
              }}
              options={ATTACHMENT_TYPES.map((value) => ({
                value,
                label: t(`attachment.type.${value}`),
              }))}
              ariaLabel={t('attachment.upload.type')}
            />
          )}
        </Field>

        <Field label={t('attachment.upload.confidentiality')} required>
          {({ id }) => (
            <Select
              id={id}
              value={confidentiality}
              onValueChange={(value) => {
                setConfidentiality(value as Confidentiality);
              }}
              options={CONFIDENTIALITY_LABELS.map((value) => ({
                value,
                label: t(`attachment.confidentiality.${value}`),
              }))}
              ariaLabel={t('attachment.upload.confidentiality')}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
